<template>
  <AppLayout>

    <!-- ══════════════════════════════════════════════════
         MOBILE LAYOUT
    ══════════════════════════════════════════════════ -->
    <div class="md:hidden flex flex-col bg-gray-50 dark:bg-slate-950 min-h-screen">

      <!-- Sticky header -->
      <div class="sticky top-0 z-10 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
        <Link href="/invoices"
          class="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 shrink-0 active:scale-95 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
          </svg>
        </Link>
        <div class="flex-1 min-w-0">
          <h1 class="text-base font-bold text-slate-800 dark:text-white">New Invoice</h1>
          <p class="text-[11px] text-slate-400 truncate">
            {{ form.action === 'sold' ? 'Sale Invoice' : 'Purchase Invoice' }}
            <span v-if="form.customer_id"> · {{ selectedCustomerLabel }}</span>
          </p>
        </div>
        <button type="button" @click="showPurchaseRate = !showPurchaseRate"
          class="w-9 h-9 flex items-center justify-center rounded-xl shrink-0 transition active:scale-95"
          :class="showPurchaseRate ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400'">
          <svg v-if="showPurchaseRate" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
          </svg>
        </button>
        <div class="text-right shrink-0">
          <p class="text-[10px] text-slate-400">Total</p>
          <p class="text-sm font-bold" :class="grandTotal > 0 ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'">
            Rs {{ formatNum(grandTotal) }}
          </p>
          <template v-if="showPurchaseRate">
            <p class="text-[9px] text-amber-500 dark:text-amber-400">Cost: Rs {{ formatNum(totalCost.toFixed(2)) }}</p>
            <p class="text-[9px] font-semibold"
              :class="totalProfit >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
              Profit: Rs {{ formatNum(totalProfit.toFixed(2)) }}
            </p>
          </template>
        </div>
      </div>

      <!-- Error -->
      <div v-if="errors?.length" class="mx-4 mt-3 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-2xl">
        <p v-for="e in errors" :key="e">• {{ e }}</p>
      </div>

      <!-- Content -->
      <div class="flex-1 px-4 py-4 space-y-3 pb-48">

        <!-- Invoice type toggle -->
        <div class="flex rounded-2xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 p-1 gap-1">
          <button type="button" @click="form.action = 'sold'"
            class="flex-1 py-2.5 rounded-xl text-sm font-semibold transition"
            :class="form.action === 'sold'
              ? 'bg-emerald-500 text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400'">
            Sale
          </button>
          <button type="button" @click="form.action = 'purchased'"
            class="flex-1 py-2.5 rounded-xl text-sm font-semibold transition"
            :class="form.action === 'purchased'
              ? 'bg-blue-500 text-white shadow-sm'
              : 'text-slate-500 dark:text-slate-400'">
            Purchase
          </button>
        </div>

        <!-- Details card -->
        <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 divide-y divide-slate-50 dark:divide-slate-800">

          <!-- Customer row -->
          <button type="button" @click="mobileCustomerSheetOpen = true; customerSearch = ''"
            class="w-full flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 dark:active:bg-slate-800 transition rounded-t-2xl">
            <div class="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
              :class="form.customer_id ? 'bg-indigo-100 dark:bg-indigo-900/50' : 'bg-slate-100 dark:bg-slate-800'">
              <svg class="w-4.5 h-4.5 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
                :class="form.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-400'">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
              </svg>
            </div>
            <div class="flex-1 text-left min-w-0">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Customer *</p>
              <p class="text-sm font-semibold mt-0.5 truncate"
                :class="form.customer_id ? 'text-slate-800 dark:text-white' : 'text-slate-400'">
                {{ selectedCustomerLabel }}
              </p>
            </div>
            <svg class="w-4 h-4 text-slate-300 dark:text-slate-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
            </svg>
          </button>

          <!-- Date row -->
          <label class="flex items-center gap-3 px-4 py-3.5 cursor-pointer">
            <div class="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
              <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"/>
              </svg>
            </div>
            <div class="flex-1">
              <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Date</p>
              <input v-model="form.target_date" type="date"
                class="text-sm font-semibold text-slate-800 dark:text-white bg-transparent border-none focus:outline-none w-full mt-0.5" />
            </div>
          </label>

          <!-- Notes row -->
          <div class="px-4 py-3.5">
            <div class="flex items-start gap-3">
              <div class="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 mt-0.5">
                <svg class="w-4 h-4 text-slate-500 dark:text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z"/>
                </svg>
              </div>
              <div class="flex-1">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Notes</p>
                <textarea v-model="form.notes" rows="2" placeholder="Optional note..."
                  class="w-full text-sm text-slate-800 dark:text-white bg-transparent border-none focus:outline-none resize-none placeholder-slate-400"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Items header -->
        <div class="flex items-center justify-between">
          <p class="text-xs font-bold text-slate-400 uppercase tracking-wide">
            Items <span v-if="form.items.length > 0" class="text-indigo-500">{{ form.items.length }}</span>
          </p>
          <button type="button" @click="addItem"
            class="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400
                   bg-indigo-50 dark:bg-indigo-950 px-3.5 py-2 rounded-full transition active:scale-95">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            Add Item
          </button>
        </div>

        <!-- Empty state -->
        <div v-if="form.items.length === 0"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 py-14 text-center">
          <div class="w-12 h-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"/>
            </svg>
          </div>
          <p class="text-slate-500 dark:text-slate-400 text-sm font-medium">No items yet</p>
          <p class="text-slate-400 text-xs mt-0.5">Tap "Add Item" to begin</p>
        </div>

        <!-- Item cards -->
        <div class="space-y-3">
          <div v-for="(item, idx) in form.items" :key="item._key"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">

            <!-- Product row -->
            <button type="button" @click="openMobileProductSheet(idx)"
              class="w-full flex items-center justify-between px-4 py-3 border-b border-slate-50 dark:border-slate-800 active:bg-slate-50 dark:active:bg-slate-800 transition">
              <div class="text-left min-w-0 flex-1">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Product</p>
                <p class="text-sm font-semibold mt-0.5 truncate"
                  :class="item.product_id ? 'text-slate-800 dark:text-white' : 'text-slate-400'">
                  {{ item.product_id ? productName(item.product_id) : 'Select product' }}
                </p>
              </div>
              <div class="flex items-center gap-2 shrink-0 ml-2">
                <button type="button" @click.stop="removeItem(idx)"
                  class="w-7 h-7 flex items-center justify-center rounded-full bg-red-50 dark:bg-red-950 text-red-400 active:scale-90 transition">
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
                <svg class="w-4 h-4 text-slate-300 dark:text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                </svg>
              </div>
            </button>

            <!-- Qty / Rate / Total row -->
            <div class="grid grid-cols-3 divide-x divide-slate-50 dark:divide-slate-800">
              <!-- Qty with stepper -->
              <div class="px-3 py-3">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Qty</p>
                <div class="flex items-center gap-1.5">
                  <button type="button"
                    @click="item.quantity = Math.max(1, (Number(item.quantity) || 1) - 1)"
                    class="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center text-base font-bold leading-none active:scale-90 transition">
                    −
                  </button>
                  <input v-model.number="item.quantity" type="number" min="1" step="1"
                    class="w-7 text-center text-sm font-bold text-slate-800 dark:text-white bg-transparent border-none focus:outline-none" />
                  <button type="button"
                    @click="item.quantity = (Number(item.quantity) || 0) + 1"
                    class="w-6 h-6 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center text-base font-bold leading-none active:scale-90 transition">
                    +
                  </button>
                </div>
              </div>

              <!-- Rate -->
              <div class="px-3 py-3">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Rate</p>
                <input v-model.number="item.rate" type="number" min="0" step="0.01" placeholder="0"
                  class="w-full text-sm font-bold text-slate-800 dark:text-white bg-transparent border-none focus:outline-none placeholder-slate-300" />
                <template v-if="showPurchaseRate && item.product_id">
                  <p v-if="item.purchase_rate" class="text-[9px] text-amber-500 dark:text-amber-400 mt-1">
                    Purchase: Rs {{ formatNum(item.purchase_rate) }}
                  </p>
                  <p v-if="props.products.find(p => p.id === item.product_id)?.sale_rate" class="text-[9px] text-emerald-500 dark:text-emerald-400 mt-0.5">
                    Sale Rate: Rs {{ formatNum(props.products.find(p => p.id === item.product_id)?.sale_rate) }}
                  </p>
                  <p v-if="form.action === 'sold' && getLastSoldPrice(item.product_id)" class="text-[9px] text-blue-500 dark:text-blue-400 mt-0.5">
                    Last Sold: Rs {{ formatNum(getLastSoldPrice(item.product_id)) }}
                  </p>
                  <p v-if="form.action === 'purchased' && getLastPurchasedPrice(item.product_id)" class="text-[9px] text-purple-500 dark:text-purple-400 mt-0.5">
                    Last Purchased: Rs {{ formatNum(getLastPurchasedPrice(item.product_id)) }}
                  </p>
                </template>
              </div>

              <!-- Total -->
              <div class="px-3 py-3">
                <p class="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-2">Total</p>
                <p class="text-sm font-bold"
                  :class="item.quantity && item.rate ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-300 dark:text-slate-600'">
                  {{ item.quantity && item.rate ? 'Rs ' + formatNum((Number(item.quantity) * Number(item.rate)).toFixed(2)) : '—' }}
                </p>
                <template v-if="showPurchaseRate && item.purchase_rate && item.quantity">
                  <p class="text-[9px] text-amber-500 dark:text-amber-400 mt-1">
                    Cost: Rs {{ formatNum((Number(item.quantity) * Number(item.purchase_rate)).toFixed(2)) }}
                  </p>
                  <p class="text-[9px] mt-0.5"
                    :class="(item.quantity * item.rate) - (item.quantity * item.purchase_rate) >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                    Profit: Rs {{ formatNum(((Number(item.quantity) * Number(item.rate)) - (Number(item.quantity) * Number(item.purchase_rate))).toFixed(2)) }}
                  </p>
                </template>
              </div>
            </div>
          </div>
        </div>

      </div>

      <!-- Fixed bottom save bar -->
      <div class="fixed bottom-16 inset-x-0 z-30 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 px-4 pt-3 pb-3">
        <div class="flex items-center gap-3">
          <div class="shrink-0">
            <p class="text-xs text-slate-400">{{ form.items.length }} item{{ form.items.length !== 1 ? 's' : '' }}</p>
            <p class="text-base font-extrabold text-slate-800 dark:text-white">Rs {{ formatNum(grandTotal) }}</p>
            <template v-if="showPurchaseRate">
              <p class="text-[10px] text-amber-500 dark:text-amber-400">Cost: Rs {{ formatNum(totalCost.toFixed(2)) }}</p>
              <p class="text-[10px] font-semibold"
                :class="totalProfit >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                Profit: Rs {{ formatNum(totalProfit.toFixed(2)) }}
              </p>
            </template>
          </div>
          <button type="button" @click="submit"
            :disabled="loading || !form.customer_id || form.items.length === 0"
            class="flex-1 py-3.5 rounded-2xl text-sm font-bold text-white transition active:scale-[0.98] disabled:opacity-50"
            :class="form.action === 'sold' ? 'bg-emerald-500 disabled:bg-slate-300' : 'bg-blue-500 disabled:bg-slate-300'">
            {{ loading ? 'Saving...' : 'Save Invoice' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════
         DESKTOP LAYOUT
    ══════════════════════════════════════════════════ -->
    <div class="hidden md:block p-6">

      <!-- Header -->
      <div class="flex items-center gap-3 mb-6">
        <Link href="/invoices"
          class="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 dark:border-slate-700
                 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition shrink-0">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5"/>
          </svg>
        </Link>
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">New Invoice</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">Create a purchase or sale invoice</p>
        </div>
      </div>

      <!-- Error -->
      <div v-if="errors?.length" class="mb-5 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-for="e in errors" :key="e">• {{ e }}</p>
      </div>

      <form @submit.prevent="submit">
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-5">

          <!-- LEFT: Invoice details -->
          <div class="lg:col-span-1 space-y-4 lg:min-w-[280px]">

            <!-- Invoice type toggle -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Invoice Type</p>
              <div class="flex rounded-xl bg-slate-100 dark:bg-slate-800 p-1 gap-1">
                <button type="button" @click="form.action = 'sold'"
                  :class="form.action === 'sold'
                    ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-sm font-semibold'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'"
                  class="flex-1 py-2 rounded-lg text-sm transition">
                  Sale Invoice
                </button>
                <button type="button" @click="form.action = 'purchased'"
                  :class="form.action === 'purchased'
                    ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm font-semibold'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'"
                  class="flex-1 py-2 rounded-lg text-sm transition">
                  Purchase Invoice
                </button>
              </div>
            </div>

            <!-- Customer + Date + Notes -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-4">
              <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide">Details</p>

              <!-- Customer searchable dropdown -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Customer <span class="text-red-400">*</span></label>
                <div class="relative" ref="customerDropdownRef">
                  <button type="button" @click="toggleCustomerDropdown"
                    class="w-full flex items-center justify-between bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                           text-slate-800 dark:text-white rounded-xl px-3 py-2.5 text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 transition">
                    <span :class="form.customer_id ? '' : 'text-slate-400'">{{ selectedCustomerLabel }}</span>
                    <svg class="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </button>
                  <div v-if="customerDropdownOpen"
                    class="absolute z-50 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
                    <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                      <input v-model="customerSearch" type="text" placeholder="Search by name or shop..."
                        class="w-full bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400
                               rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        @click.stop
                        @keydown.down.prevent="moveCustomer(1)"
                        @keydown.up.prevent="moveCustomer(-1)"
                        @keydown.enter.prevent="confirmCustomer"
                        @keydown.escape="customerDropdownOpen = false" />
                    </div>
                    <div class="max-h-44 overflow-y-auto" ref="customerListRef">
                      <button v-for="(c, idx) in filteredCustomers" :key="c.id" type="button"
                        :ref="el => { if (el) customerItemRefs[idx] = el }"
                        @click="selectCustomer(c)"
                        class="w-full text-left px-3 py-2.5 text-sm transition"
                        :class="idx === activeCustomerIdx
                          ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                          : form.customer_id === c.id
                            ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                        <p>{{ c.name }}</p>
                        <p class="text-xs text-slate-400">{{ c.shop_name }} · {{ c.city_name }}</p>
                      </button>
                      <p v-if="filteredCustomers.length === 0" class="px-3 py-4 text-sm text-slate-400 text-center">No customers found</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Date -->
              <DatePicker v-model="form.target_date" label="Date" />

              <!-- Notes -->
              <div class="flex flex-col gap-1">
                <label class="text-xs font-medium text-slate-500 dark:text-slate-400">Notes</label>
                <textarea v-model="form.notes" rows="3" placeholder="Optional notes..."
                  class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                         text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-3 py-2.5 text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none transition"></textarea>
              </div>
            </div>

            <!-- Summary card -->
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4">
              <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Summary</p>
              <div class="space-y-2 text-sm">
                <div class="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Items</span>
                  <span>{{ form.items.length }}</span>
                </div>
                <div class="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Total Qty</span>
                  <span>{{ totalQty }}</span>
                </div>
                <div class="border-t border-slate-100 dark:border-slate-800 pt-2 flex justify-between font-bold text-slate-800 dark:text-white text-base">
                  <span>Grand Total</span>
                  <span>Rs {{ formatNum(grandTotal) }}</span>
                </div>
              </div>

              <div class="flex gap-2 mt-4">
                <Link href="/invoices"
                  class="flex-1 py-2.5 text-center rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                         text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
                  Cancel
                </Link>
                <button type="submit" :disabled="loading || !form.customer_id || form.items.length === 0"
                  class="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition
                         bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed">
                  {{ loading ? 'Saving...' : 'Save Invoice' }}
                </button>
              </div>
            </div>

          </div>

          <!-- RIGHT: Line items -->
          <div class="lg:col-span-3">
            <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800">

              <!-- Items header -->
              <div class="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800 rounded-t-2xl">
                <p class="text-xs font-semibold text-slate-400 uppercase tracking-wide">Line Items</p>
                <div class="flex items-center gap-2">
                  <!-- Show/hide purchase rate -->
                  <div class="relative group">
                    <button type="button" @click="showPurchaseRate = !showPurchaseRate"
                      class="w-7 h-7 flex items-center justify-center rounded-lg transition"
                      :class="showPurchaseRate
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                        : 'text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'">
                      <!-- Eye open -->
                      <svg v-if="showPurchaseRate" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.964-7.178z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      </svg>
                      <!-- Eye closed -->
                      <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"/>
                      </svg>
                    </button>
                    <div class="absolute right-0 top-full mt-1.5 z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <div class="bg-slate-800 dark:bg-slate-700 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                        {{ showPurchaseRate ? 'Hide purchase rate' : 'Show purchase rate' }}
                        <div class="absolute right-2.5 -top-1 w-2 h-2 bg-slate-800 dark:bg-slate-700 rotate-45"></div>
                      </div>
                    </div>
                  </div>

                  <button type="button" @click="addItem"
                    class="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 dark:text-indigo-400
                           hover:bg-indigo-50 dark:hover:bg-indigo-950 px-3 py-1.5 rounded-lg transition">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                    </svg>
                    Add Item
                  </button>
                </div>
              </div>

              <!-- Empty items state -->
              <div v-if="form.items.length === 0" class="flex flex-col items-center justify-center py-16 text-center px-5">
                <div class="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                  <svg class="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3 3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"/>
                  </svg>
                </div>
                <p class="text-slate-500 dark:text-slate-400 text-sm font-medium">No items added yet</p>
                <p class="text-slate-400 dark:text-slate-500 text-xs mt-1">Click "Add Item" to start building the invoice</p>
              </div>

              <!-- Items table header -->
              <div v-if="form.items.length > 0" class="grid grid-cols-12 gap-2 px-5 py-2.5 bg-slate-50 dark:bg-slate-800/50 text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100 dark:border-slate-800">
                <div class="col-span-5">Product</div>
                <div class="col-span-2 text-center">Qty</div>
                <div class="col-span-2 text-right">Rate</div>
                <div class="col-span-2 text-right">Total</div>
                <div class="col-span-1"></div>
              </div>

              <!-- Items list -->
              <div class="divide-y divide-slate-50 dark:divide-slate-800">
                <div v-for="(item, idx) in form.items" :key="item._key"
                  class="px-5 py-3 grid grid-cols-12 gap-2 items-start">

                  <!-- Product dropdown -->
                  <div class="col-span-5 relative" :ref="el => productDropdownRefs[idx] = el">
                    <button type="button" @click="toggleProductDropdown(idx)"
                      class="w-full flex items-center justify-between bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                             text-slate-800 dark:text-white rounded-xl px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-500 transition">
                      <span :class="item.product_id ? '' : 'text-slate-400'" class="truncate">
                        {{ item.product_id ? productName(item.product_id) : 'Select product' }}
                      </span>
                      <svg class="w-3 h-3 text-slate-400 shrink-0 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                      </svg>
                    </button>
                    <div v-if="activeProductDropdown === idx"
                      class="absolute z-50 mt-1 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden">
                      <div class="p-2 border-b border-slate-100 dark:border-slate-700">
                        <input v-model="productSearch" type="text" placeholder="Search product..."
                          class="w-full bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-white placeholder-slate-400
                                 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          @click.stop
                          @keydown.down.prevent="moveProduct(1)"
                          @keydown.up.prevent="moveProduct(-1)"
                          @keydown.enter.prevent="confirmProduct(idx)"
                          @keydown.escape="activeProductDropdown = null" />
                      </div>
                      <div class="max-h-48 overflow-y-auto">
                        <button v-for="(p, pidx) in filteredProducts" :key="p.id" type="button"
                          :ref="el => { if (el) productItemRefs[pidx] = el }"
                          @click="selectProduct(idx, p)"
                          class="w-full text-left px-3 py-2.5 text-sm transition"
                          :class="pidx === activeProductIdx
                            ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 font-semibold'
                            : item.product_id === p.id
                              ? 'text-indigo-600 dark:text-indigo-400 font-semibold'
                              : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'">
                          <p>{{ p.name }}</p>
                          <p class="text-xs text-slate-400">
                            {{ form.action === 'sold' ? 'Rate: Rs ' + (p.sale_rate ? formatNum(p.sale_rate) : '—') : 'Purchase: Rs ' + (p.purchase_price ? formatNum(p.purchase_price) : '—') }}
                          </p>
                        </button>
                        <p v-if="filteredProducts.length === 0" class="px-3 py-4 text-sm text-slate-400 text-center">No products found</p>
                      </div>
                    </div>
                  </div>

                  <!-- Qty -->
                  <div class="col-span-2">
                    <input v-model.number="item.quantity" type="number" min="1" step="1" placeholder="Qty"
                      class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                             text-slate-800 dark:text-white text-center rounded-xl px-2 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
                  </div>

                  <!-- Rate -->
                  <div class="col-span-2">
                    <input v-model.number="item.rate" type="number" min="0" step="0.01" placeholder="Rate"
                      class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                             text-slate-800 dark:text-white text-right rounded-xl px-2 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
                    <template v-if="showPurchaseRate && item.product_id">
                      <p v-if="item.purchase_rate" class="text-[10px] text-amber-500 dark:text-amber-400 text-right mt-1 px-1">
                        Purchase: Rs {{ formatNum(item.purchase_rate) }}
                      </p>
                      <p v-if="props.products.find(p => p.id === item.product_id)?.sale_rate" class="text-[10px] text-emerald-500 dark:text-emerald-400 text-right mt-0.5 px-1">
                        Sale Rate: Rs {{ formatNum(props.products.find(p => p.id === item.product_id)?.sale_rate) }}
                      </p>
                      <p v-if="form.action === 'sold' && getLastSoldPrice(item.product_id)" class="text-[10px] text-blue-500 dark:text-blue-400 text-right mt-0.5 px-1">
                        Last Sold: Rs {{ formatNum(getLastSoldPrice(item.product_id)) }}
                      </p>
                      <p v-if="form.action === 'purchased' && getLastPurchasedPrice(item.product_id)" class="text-[10px] text-purple-500 dark:text-purple-400 text-right mt-0.5 px-1">
                        Last Purchased: Rs {{ formatNum(getLastPurchasedPrice(item.product_id)) }}
                      </p>
                    </template>
                  </div>

                  <!-- Line total -->
                  <div class="col-span-2 text-right">
                    <span class="text-sm font-semibold text-slate-800 dark:text-white">
                      {{ item.quantity && item.rate ? 'Rs ' + formatNum((item.quantity * item.rate).toFixed(2)) : '—' }}
                    </span>
                    <template v-if="showPurchaseRate && item.purchase_rate && item.quantity">
                      <p class="text-[10px] text-amber-500 dark:text-amber-400 mt-1">
                        Cost: Rs {{ formatNum((Number(item.quantity) * Number(item.purchase_rate)).toFixed(2)) }}
                      </p>
                      <p class="text-[10px] mt-0.5"
                        :class="(item.quantity * item.rate) - (item.quantity * item.purchase_rate) >= 0
                          ? 'text-emerald-500 dark:text-emerald-400'
                          : 'text-red-500 dark:text-red-400'">
                        Profit: Rs {{ formatNum(((Number(item.quantity) * Number(item.rate)) - (Number(item.quantity) * Number(item.purchase_rate))).toFixed(2)) }}
                      </p>
                    </template>
                  </div>

                  <!-- Remove -->
                  <div class="col-span-1 flex justify-end self-center">
                    <button type="button" @click="removeItem(idx)"
                      class="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950 transition">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Footer totals -->
              <div v-if="form.items.length > 0"
                class="px-5 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30 flex justify-end rounded-b-2xl">
                <div class="text-right space-y-1">
                  <p class="text-xs text-slate-400">{{ form.items.length }} item{{ form.items.length !== 1 ? 's' : '' }} · {{ totalQty }} units</p>
                  <p class="text-lg font-bold text-slate-800 dark:text-white">Rs {{ formatNum(grandTotal) }}</p>
                  <template v-if="showPurchaseRate">
                    <p class="text-xs text-amber-500 dark:text-amber-400">Cost: Rs {{ formatNum(totalCost.toFixed(2)) }}</p>
                    <p class="text-xs font-semibold"
                      :class="totalProfit >= 0 ? 'text-emerald-500 dark:text-emerald-400' : 'text-red-500 dark:text-red-400'">
                      Profit: Rs {{ formatNum(totalProfit.toFixed(2)) }}
                    </p>
                  </template>
                </div>
              </div>

            </div>
          </div>

        </div>
      </form>

    </div>

    <!-- ══════════════════════════════════════════════════
         MOBILE: Customer bottom sheet
    ══════════════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition enter-active-class="transition-opacity duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileCustomerSheetOpen" class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileCustomerSheetOpen = false" />
      </Transition>
      <Transition enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in" leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileCustomerSheetOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 85vh" @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-bold text-slate-800 dark:text-white">Select Customer</p>
            <button @click="mobileCustomerSheetOpen = false"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- Search -->
          <div class="px-4 py-3 shrink-0">
            <div class="relative">
              <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/>
              </svg>
              <input v-model="customerSearch" type="text" placeholder="Search by name or shop..."
                autocomplete="off" autocorrect="off" spellcheck="false"
                class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-4 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
            </div>
          </div>
          <!-- List -->
          <div class="overflow-y-auto flex-1 px-3 pb-4">
            <button type="button" @click="form.customer_id = null; mobileCustomerSheetOpen = false"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 transition active:scale-[0.98]"
              :class="!form.customer_id ? 'bg-indigo-50 dark:bg-indigo-950' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'">
              <span class="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 text-slate-400">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </span>
              <span class="text-sm font-semibold" :class="!form.customer_id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-500 dark:text-slate-400'">All Customers</span>
            </button>
            <button v-for="c in filteredCustomers" :key="c.id" type="button"
              @click="selectCustomer(c); mobileCustomerSheetOpen = false"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 transition active:scale-[0.98]"
              :class="form.customer_id === c.id ? 'bg-indigo-50 dark:bg-indigo-950' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'">
              <span class="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-100 to-violet-100 dark:from-indigo-900/60 dark:to-violet-900/60 flex items-center justify-center shrink-0 text-sm font-bold text-indigo-600 dark:text-indigo-400">
                {{ c.name.charAt(0).toUpperCase() }}
              </span>
              <div class="min-w-0 flex-1 text-left">
                <p class="text-sm font-semibold truncate" :class="form.customer_id === c.id ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-800 dark:text-white'">{{ c.name }}</p>
                <p class="text-xs text-slate-400 truncate mt-0.5">{{ c.shop_name }}</p>
              </div>
              <svg v-if="form.customer_id === c.id" class="w-4 h-4 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>
            <p v-if="filteredCustomers.length === 0" class="text-center text-slate-400 text-sm py-10">No customers found</p>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- ══════════════════════════════════════════════════
         MOBILE: Product bottom sheet
    ══════════════════════════════════════════════════ -->
    <Teleport to="body">
      <Transition enter-active-class="transition-opacity duration-300" enter-from-class="opacity-0" enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200" leave-from-class="opacity-100" leave-to-class="opacity-0">
        <div v-if="mobileProductSheetOpen" class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
          @click="mobileProductSheetOpen = false" />
      </Transition>
      <Transition enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
        enter-from-class="translate-y-full" enter-to-class="translate-y-0"
        leave-active-class="transition-transform duration-200 ease-in" leave-from-class="translate-y-0" leave-to-class="translate-y-full">
        <div v-if="mobileProductSheetOpen"
          class="md:hidden fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl flex flex-col"
          style="max-height: 85vh" @click.stop>
          <!-- Handle -->
          <div class="flex justify-center pt-3 pb-1 shrink-0">
            <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
          </div>
          <!-- Header -->
          <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <p class="font-bold text-slate-800 dark:text-white">Select Product</p>
            <button @click="mobileProductSheetOpen = false"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <!-- Search -->
          <div class="px-4 py-3 shrink-0">
            <div class="relative">
              <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/>
              </svg>
              <input v-model="productSearch" type="text" placeholder="Search product..."
                autocomplete="off" autocorrect="off" spellcheck="false"
                class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                       text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-4 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500 transition" />
            </div>
          </div>
          <!-- List -->
          <div class="overflow-y-auto flex-1 px-3 pb-4">
            <button v-for="p in filteredProducts" :key="p.id" type="button"
              @click="selectProduct(mobileProductSheetItemIdx, p); mobileProductSheetOpen = false"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-2xl mb-1 transition active:scale-[0.98]"
              :class="mobileProductSheetItemIdx !== null && form.items[mobileProductSheetItemIdx]?.product_id === p.id
                ? 'bg-indigo-50 dark:bg-indigo-950'
                : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'">
              <span class="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center shrink-0 text-sm font-bold text-slate-500 dark:text-slate-400">
                {{ p.name.charAt(0).toUpperCase() }}
              </span>
              <div class="min-w-0 flex-1 text-left">
                <p class="text-sm font-semibold truncate"
                  :class="mobileProductSheetItemIdx !== null && form.items[mobileProductSheetItemIdx]?.product_id === p.id
                    ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-800 dark:text-white'">
                  {{ p.name }}
                </p>
                <p class="text-xs text-slate-400 mt-0.5">
                  {{ form.action === 'sold'
                    ? 'Rate: Rs ' + (p.sale_rate ? formatNum(p.sale_rate) : '—')
                    : 'Purchase: Rs ' + (p.purchase_price ? formatNum(p.purchase_price) : '—') }}
                </p>
              </div>
              <svg v-if="mobileProductSheetItemIdx !== null && form.items[mobileProductSheetItemIdx]?.product_id === p.id"
                class="w-4 h-4 text-indigo-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
              </svg>
            </button>
            <p v-if="filteredProducts.length === 0" class="text-center text-slate-400 text-sm py-10">No products found</p>
          </div>
        </div>
      </Transition>
    </Teleport>

  </AppLayout>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { Link, usePage, useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import DatePicker from '@/components/DatePicker.vue'
import { useLastSoldPrices } from '@/composables/useLastSoldPrices'
import { useLastPurchasedPrices } from '@/composables/useLastPurchasedPrices'

const props = defineProps({ customers: Array, products: Array })
const page   = usePage()
const errors = computed(() => page.props.errors)
const loading = ref(false)

// ── Last sold prices for sold invoices ──────────
const { lastSoldPrices, fetchLastSoldPrices, getLastSoldPrice } = useLastSoldPrices()

// ── Last purchased prices for purchase invoices ──────────
const { lastPurchasedPrices, fetchLastPurchasedPrices, getLastPurchasedPrice } = useLastPurchasedPrices()

// ── Form state ──────────────────────────────────────
const today = new Date().toISOString().split('T')[0]
const form = ref({
  action:      'sold',
  customer_id: null,
  target_date: today,
  notes:       '',
  items:       [],
})

let _key = 0
function addItem() {
  form.value.items.push({ _key: _key++, product_id: null, quantity: 1, rate: '', purchase_rate: 0 })
}

function removeItem(idx) {
  form.value.items.splice(idx, 1)
}

// Fetch last sold/purchased prices when customer or invoice type changes
watch(
  () => [form.value.customer_id, form.value.action],
  ([customerId, action], [oldCustomerId, oldAction]) => {
    if (action === 'sold' && customerId) {
      fetchLastSoldPrices(customerId).then(() => {
        // Update existing items with new prices if customer changed
        if (oldCustomerId !== customerId) {
          form.value.items.forEach(item => {
            if (!item.product_id) return
            const p = props.products.find(p => p.id === item.product_id)
            if (!p) return

            // Recalculate rate with new customer's last sold prices
            const lastSoldPrice = getLastSoldPrice(p.id)
            const currentSalePrice = p.sale_rate ?? 0

            if (lastSoldPrice !== null) {
              item.rate = Math.max(lastSoldPrice, currentSalePrice)
            } else {
              item.rate = currentSalePrice || ''
            }
          })
        }
      })
    } else if (action === 'purchased' && customerId) {
      fetchLastPurchasedPrices(customerId).then(() => {
        // Update existing items with new prices if customer changed
        if (oldCustomerId !== customerId) {
          form.value.items.forEach(item => {
            if (!item.product_id) return
            const p = props.products.find(p => p.id === item.product_id)
            if (!p) return

            // Recalculate rate with new customer's last purchased prices
            const lastPurchasedPrice = getLastPurchasedPrice(p.id)
            const currentPurchasePrice = p.purchase_price ?? 0

            if (lastPurchasedPrice !== null) {
              item.rate = Math.min(lastPurchasedPrice, currentPurchasePrice || lastPurchasedPrice)
            } else {
              item.rate = currentPurchasePrice || ''
            }
          })
        }
      })
    } else if (action === 'sold' && !customerId) {
      // Clear last sold prices when customer is deselected
      lastSoldPrices.value = []
    } else if (action === 'purchased' && !customerId) {
      // Clear last purchased prices when customer is deselected
      lastPurchasedPrices.value = []
    }
  },
  { immediate: false }
)

// When invoice type changes, re-fill rates from the correct price field
watch(() => form.value.action, () => {
  form.value.items.forEach(item => {
    if (!item.product_id) return
    const p = props.products.find(p => p.id === item.product_id)
    if (!p) return

    if (form.value.action === 'purchased') {
      // For purchase invoices, use the lower of last purchased price or current purchase price
      const lastPurchasedPrice = getLastPurchasedPrice(p.id)
      const currentPurchasePrice = p.purchase_price ?? 0

      if (lastPurchasedPrice !== null) {
        item.rate = Math.min(lastPurchasedPrice, currentPurchasePrice || lastPurchasedPrice)
      } else {
        item.rate = currentPurchasePrice || ''
      }
    } else {
      // For sold invoices, use the higher of last sold price or current sale price
      const lastSoldPrice = getLastSoldPrice(p.id)
      const currentSalePrice = p.sale_rate ?? 0

      if (lastSoldPrice !== null) {
        item.rate = Math.max(lastSoldPrice, currentSalePrice)
      } else {
        item.rate = currentSalePrice || ''
      }
    }
    item.purchase_rate = p.purchase_price ?? 0
  })
})

// ── Totals ──────────────────────────────────────────
const grandTotal = computed(() =>
  form.value.items.reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.rate) || 0), 0)
)
const totalQty = computed(() =>
  form.value.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0)
)
const totalCost = computed(() =>
  form.value.items.reduce((sum, i) => sum + (Number(i.quantity) || 0) * (Number(i.purchase_rate) || 0), 0)
)
const totalProfit = computed(() => grandTotal.value - totalCost.value)

function formatNum(n) {
  return Number(n).toLocaleString('en-PK')
}

function productName(id) {
  return props.products.find(p => p.id === id)?.name || ''
}

// ── Customer dropdown (desktop) ──────────────────────
const customerDropdownOpen = ref(false)
const customerSearch       = ref('')
const customerDropdownRef  = ref(null)
const customerItemRefs     = ref([])
const activeCustomerIdx    = ref(-1)

const filteredCustomers = computed(() => {
  const q = customerSearch.value.trim().toLowerCase()
  if (!q) return props.customers
  return props.customers.filter(c =>
    c.name.toLowerCase().includes(q) || c.shop_name.toLowerCase().includes(q)
  )
})

const selectedCustomerLabel = computed(() => {
  if (!form.value.customer_id) return 'Select customer'
  return props.customers.find(c => c.id === form.value.customer_id)?.name || 'Select customer'
})

function toggleCustomerDropdown() {
  customerDropdownOpen.value = !customerDropdownOpen.value
  if (customerDropdownOpen.value) { customerSearch.value = ''; activeCustomerIdx.value = -1; customerItemRefs.value = [] }
}

function selectCustomer(c) {
  form.value.customer_id     = c.id
  customerDropdownOpen.value = false
}

function moveCustomer(dir) {
  const len = filteredCustomers.value.length
  if (!len) return
  activeCustomerIdx.value = (activeCustomerIdx.value + dir + len) % len
  customerItemRefs.value[activeCustomerIdx.value]?.scrollIntoView({ block: 'nearest' })
}

function confirmCustomer() {
  if (activeCustomerIdx.value >= 0 && filteredCustomers.value[activeCustomerIdx.value]) {
    selectCustomer(filteredCustomers.value[activeCustomerIdx.value])
  }
}

watch(customerSearch, () => { activeCustomerIdx.value = -1; customerItemRefs.value = [] })

// ── Product dropdown (desktop) ────────────────────────
const activeProductDropdown = ref(null)
const productDropdownRefs   = ref([])
const productItemRefs       = ref([])
const productSearch         = ref('')
const activeProductIdx      = ref(-1)

const filteredProducts = computed(() => {
  const q = productSearch.value.trim().toLowerCase()
  if (!q) return props.products
  return props.products.filter(p => p.name.toLowerCase().includes(q))
})

function toggleProductDropdown(idx) {
  if (activeProductDropdown.value === idx) {
    activeProductDropdown.value = null
  } else {
    activeProductDropdown.value = idx
    productSearch.value         = ''
    activeProductIdx.value      = -1
    productItemRefs.value       = []
  }
}

function selectProduct(itemIdx, p) {
  if (itemIdx === null || itemIdx === undefined) return
  const item = form.value.items[itemIdx]
  if (!item) return
  item.product_id    = p.id
  item.purchase_rate = p.purchase_price ?? 0

  // Determine the rate based on invoice action
  if (form.value.action === 'purchased') {
    // For purchase invoices, use the lower of last purchased price or current purchase price
    const lastPurchasedPrice = getLastPurchasedPrice(p.id)
    const currentPurchasePrice = p.purchase_price ?? 0

    if (lastPurchasedPrice !== null) {
      // Product exists in last purchased prices - use the lower value
      item.rate = Math.min(lastPurchasedPrice, currentPurchasePrice || lastPurchasedPrice)
    } else {
      // Product not in last purchased prices - use current purchase price
      item.rate = currentPurchasePrice || ''
    }
  } else {
    // For sold invoices, use the higher of last sold price or current sale price
    const lastSoldPrice = getLastSoldPrice(p.id)
    const currentSalePrice = p.sale_rate ?? 0

    if (lastSoldPrice !== null) {
      // Product exists in last sold prices - use the higher value
      item.rate = Math.max(lastSoldPrice, currentSalePrice)
    } else {
      // Product not in last sold prices - use current sale price
      item.rate = currentSalePrice || ''
    }
  }

  activeProductDropdown.value = null
}

function moveProduct(dir) {
  const len = filteredProducts.value.length
  if (!len) return
  activeProductIdx.value = (activeProductIdx.value + dir + len) % len
  productItemRefs.value[activeProductIdx.value]?.scrollIntoView({ block: 'nearest' })
}

function confirmProduct(itemIdx) {
  if (activeProductIdx.value >= 0 && filteredProducts.value[activeProductIdx.value]) {
    selectProduct(itemIdx, filteredProducts.value[activeProductIdx.value])
  }
}

watch(productSearch, () => { activeProductIdx.value = -1; productItemRefs.value = [] })

// ── Purchase rate visibility ─────────────────────────
const showPurchaseRate = ref(false)

// ── Mobile sheets ────────────────────────────────────
const mobileCustomerSheetOpen    = ref(false)
const mobileProductSheetOpen     = ref(false)
const mobileProductSheetItemIdx  = ref(null)

function openMobileProductSheet(idx) {
  mobileProductSheetItemIdx.value = idx
  productSearch.value             = ''
  mobileProductSheetOpen.value    = true
}

// ── Click outside (desktop) ──────────────────────────
function onClickOutside(e) {
  if (customerDropdownRef.value && !customerDropdownRef.value.contains(e.target)) {
    customerDropdownOpen.value = false
  }
  if (activeProductDropdown.value !== null) {
    const ref = productDropdownRefs.value[activeProductDropdown.value]
    if (ref && !ref.contains(e.target)) activeProductDropdown.value = null
  }
}

onMounted(()    => document.addEventListener('click', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside))

// ── Submit ───────────────────────────────────────────
function submit() {
  loading.value = true
  const payload = useForm({
    invoice: {
      action:       form.value.action,
      customer_id:  form.value.customer_id,
      target_date:  form.value.target_date,
      notes:        form.value.notes,
      invoice_items: form.value.items.map(i => ({
        product_id:    i.product_id,
        quantity:      i.quantity,
        rate:          i.rate,
        purchase_rate: i.purchase_rate ?? 0,
      })),
    }
  })
  payload.post('/invoices', { onFinish: () => { loading.value = false } })
}
</script>
